import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Client-side pagination for dashboard tables. Pass a MEMOIZED items array so
// the page only resets when the underlying data (or filter) actually changes.
export function usePaged(items, pageSize = 25) {
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [items]);
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const cur = Math.min(page, totalPages);
  const from = total === 0 ? 0 : (cur - 1) * pageSize + 1;
  const to = Math.min(cur * pageSize, total);
  const pageItems = items.slice((cur - 1) * pageSize, cur * pageSize);
  return { pageItems, page: cur, setPage, totalPages, total, from, to, pageSize };
}

export function TablePager({ pager, testId, label = "rows" }) {
  const { page, setPage, totalPages, total, from, to } = pager;
  if (total <= pager.pageSize) return null; // only show when there's more than one page
  return (
    <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-black/5" data-testid={testId}>
      <span className="text-ink-3 text-xs">
        Showing <b className="text-ink">{from}–{to}</b> of <b className="text-ink">{total}</b> {label}
      </span>
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
          data-testid={`${testId}-prev`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-black/10 bg-white/70 text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:border-black/25 transition-colors"
        >
          <ChevronLeft size={14} /> Prev
        </button>
        <span className="text-xs text-ink-2 tabular-nums px-1" data-testid={`${testId}-status`}>Page {page} / {totalPages}</span>
        <button
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
          data-testid={`${testId}-next`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-black/10 bg-white/70 text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:border-black/25 transition-colors"
        >
          Next <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
